import { useMemo, useRef, useState, useEffect } from 'react'
import { CheckCircle2, Clock, AlertTriangle, Users, Wrench } from 'lucide-react'
import { FaseDetail, StatoCommessa } from '../../types/commesse'

interface GanttChartProps {
  fasi: FaseDetail[]
  articoli?: { id: string; ordine: number }[]
  dataInizio: string | null
  dataConsegna: string
  stato: StatoCommessa
  onDurataChange?: (faseId: string, newDurata: number) => void
  onReorder?: (orderedFaseIds: string[]) => void
}

const ROW_HEIGHT = 48

export function GanttChart({
  fasi, articoli = [], dataInizio, dataConsegna, stato,
  onDurataChange, onReorder,
}: GanttChartProps) {
  const isRelative = !dataInizio || stato === 'pianificazione'

  // mappa articolo_id → numero (ordine + 1)
  const artNumMap = useMemo(() => {
    const m: Record<string, number> = {}
    articoli.forEach(a => { m[a.id] = a.ordine + 1 })
    return m
  }, [articoli])

  const { bars, totalDays, todayOffset, consegnaOffset, dayWidth } = useMemo(() => {
    if (fasi.length === 0) {
      return { bars: [], totalDays: 0, todayOffset: -1, consegnaOffset: -1, dayWidth: 0 }
    }

    if (isRelative) {
      const maxOffset = Math.max(...fasi.map(f => f.offset_fine), 7)
      const totalDays = maxOffset + 3
      const dayWidth = Math.max(30, Math.min(60, 900 / totalDays))
      const bars = fasi.map(fase => ({
        fase,
        startDay: fase.offset_inizio,
        plannedDays: fase.durata_prevista,
        actualDays: Math.max(fase.durata_prevista, fase.in_ritardo ? fase.giorni_effettivi : fase.durata_prevista),
      }))
      return { bars, totalDays, todayOffset: -1, consegnaOffset: -1, dayWidth }
    } else {
      const projectStart = new Date(dataInizio!)
      const today = new Date(); today.setHours(0, 0, 0, 0)
      const consegna = new Date(dataConsegna)
      let maxEnd = consegna
      fasi.forEach(f => {
        if (f.data_fine_prevista) {
          const fe = new Date(f.data_fine_prevista)
          if (f.in_ritardo && f.giorni.length > 0) {
            const lg = new Date(f.giorni[f.giorni.length - 1].data)
            if (lg > fe) fe.setTime(lg.getTime() + 86400000)
          }
          if (fe > maxEnd) maxEnd = fe
        }
      })
      const endDate = new Date(maxEnd); endDate.setDate(endDate.getDate() + 3)
      const totalDays = Math.max(Math.ceil((endDate.getTime() - projectStart.getTime()) / 86400000), 7)
      const dayWidth = Math.max(30, Math.min(60, 900 / totalDays))
      const todayOffset = Math.max(0, (today.getTime() - projectStart.getTime()) / 86400000)
      const consegnaOffset = (consegna.getTime() - projectStart.getTime()) / 86400000
      const bars = fasi.map(fase => {
        const startDay = fase.data_inizio_prevista
          ? (new Date(fase.data_inizio_prevista).getTime() - projectStart.getTime()) / 86400000
          : fase.offset_inizio
        const plannedDays = fase.durata_prevista
        const actualDays = Math.max(plannedDays, fase.in_ritardo ? fase.giorni_effettivi : plannedDays)
        return { fase, startDay, plannedDays, actualDays }
      })
      return { bars, totalDays, todayOffset, consegnaOffset, dayWidth }
    }
  }, [fasi, dataInizio, dataConsegna, stato, isRelative])

  // ─── DRAG STATE ───
  const dragRef = useRef<{
    type: 'resize' | 'reorder'
    faseId: string
    faseIndex: number
    startX: number
    startY: number
    startDuration: number
    scrollLeft: number
  } | null>(null)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [resizePreview, setResizePreview] = useState<{ faseId: string; durata: number } | null>(null)
  const [reorderPreview, setReorderPreview] = useState<{ fromIndex: number; toIndex: number } | null>(null)

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return
      const { type, startX, startY, startDuration, faseIndex, scrollLeft } = dragRef.current
      const scrollEl = scrollContainerRef.current
      const currentScroll = scrollEl?.scrollLeft ?? 0

      if (type === 'resize') {
        const deltaX = (e.clientX - startX) + (currentScroll - scrollLeft)
        const deltaDays = Math.round(deltaX / dayWidth)
        const newDurata = Math.max(1, startDuration + deltaDays)
        setResizePreview({ faseId: dragRef.current.faseId, durata: newDurata })
      } else {
        const deltaY = e.clientY - startY
        const shift = Math.round(deltaY / ROW_HEIGHT)
        const toIndex = Math.max(0, Math.min(bars.length - 1, faseIndex + shift))
        setReorderPreview({ fromIndex: faseIndex, toIndex })
      }
    }

    const onUp = (e: MouseEvent) => {
      if (!dragRef.current) return
      const { type, faseId, faseIndex, startX, startDuration, scrollLeft } = dragRef.current
      const scrollEl = scrollContainerRef.current
      const currentScroll = scrollEl?.scrollLeft ?? 0

      if (type === 'resize' && onDurataChange) {
        const deltaX = (e.clientX - startX) + (currentScroll - scrollLeft)
        const deltaDays = Math.round(deltaX / dayWidth)
        const newDurata = Math.max(1, startDuration + deltaDays)
        if (newDurata !== startDuration) onDurataChange(faseId, newDurata)
      } else if (type === 'reorder' && onReorder) {
        const deltaY = e.clientY - dragRef.current.startY
        const shift = Math.round(deltaY / ROW_HEIGHT)
        const toIndex = Math.max(0, Math.min(bars.length - 1, faseIndex + shift))
        if (toIndex !== faseIndex) {
          const newOrder = bars.map(b => b.fase.id)
          const [moved] = newOrder.splice(faseIndex, 1)
          newOrder.splice(toIndex, 0, moved)
          onReorder(newOrder)
        }
      }
      dragRef.current = null
      setResizePreview(null)
      setReorderPreview(null)
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [bars, dayWidth, onDurataChange, onReorder])

  if (fasi.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">
        <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
        <p>Nessuna fase definita. Aggiungi fasi per visualizzare il Gantt.</p>
      </div>
    )
  }

  const dayLabels: { day: number; label: string }[] = []
  const step = totalDays > 30 ? 7 : totalDays > 14 ? 3 : 1
  if (isRelative) {
    for (let i = 0; i <= totalDays; i += step) dayLabels.push({ day: i, label: `G${i + 1}` })
  } else {
    const projectStart = new Date(dataInizio!)
    for (let i = 0; i <= totalDays; i += step) {
      const d = new Date(projectStart); d.setDate(d.getDate() + i)
      dayLabels.push({ day: i, label: d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) })
    }
  }

  const getBarBgColor = (fase: FaseDetail) => {
    if (isRelative) return 'bg-gray-200'
    if (fase.in_ritardo) return 'bg-red-100'
    if (fase.completata) return 'bg-green-100'
    if (fase.giorni_effettivi > 0) return 'bg-blue-100'
    return 'bg-gray-100'
  }
  const getBarColor = (fase: FaseDetail) => {
    if (isRelative) return 'bg-gray-400'
    if (fase.in_ritardo) return 'bg-red-500'
    if (fase.completata) return 'bg-green-500'
    if (fase.giorni_effettivi > 0) return 'bg-blue-500'
    return 'bg-gray-300'
  }
  const getStatusIcon = (fase: FaseDetail) => {
    if (isRelative) return <Clock className="w-3.5 h-3.5 text-gray-400" />
    if (fase.in_ritardo) return <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
    if (fase.completata) return <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
    if (fase.giorni_effettivi > 0) return <Clock className="w-3.5 h-3.5 text-blue-500" />
    return <Clock className="w-3.5 h-3.5 text-gray-400" />
  }

  const chartWidth = totalDays * dayWidth

  // ordine barre con drag preview
  const displayBars = reorderPreview
    ? (() => {
        const copy = [...bars]
        const [moved] = copy.splice(reorderPreview.fromIndex, 1)
        copy.splice(reorderPreview.toIndex, 0, moved)
        return copy
      })()
    : bars

  const canDrag = !!onReorder || !!onDurataChange

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden select-none">
      <div className="overflow-x-auto" ref={scrollContainerRef}>
        <div className="min-w-fit">
          {/* Header */}
          <div className="flex border-b border-gray-200">
            <div className="w-64 flex-shrink-0 px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase">
              Fase
            </div>
            <div className="relative" style={{ width: chartWidth }}>
              {dayLabels.map((dl, i) => (
                <div
                  key={i}
                  className="text-xs text-gray-400 py-2 text-center border-l border-gray-100 absolute"
                  style={{ left: dl.day * dayWidth, width: step * dayWidth }}
                >
                  {dl.label}
                </div>
              ))}
            </div>
          </div>

          {/* Righe */}
          {displayBars.map(({ fase, startDay, plannedDays, actualDays }, rowIdx) => {
            const isDraggingThis = dragRef.current?.faseId === fase.id
            const artNum = fase.articolo_id ? artNumMap[fase.articolo_id] : null
            const numPartecipanti = Math.max(1, fase.partecipanti_ids?.length || 0)
            const oreFase = fase.durata_prevista * numPartecipanti
            const durataMostrata = resizePreview?.faseId === fase.id ? resizePreview.durata : plannedDays

            return (
              <div
                key={fase.id}
                className={`flex border-b border-gray-100 transition-colors ${isDraggingThis ? 'opacity-60 bg-blue-50' : 'hover:bg-gray-50'}`}
                style={{ height: ROW_HEIGHT }}
              >
                {/* Label */}
                <div
                  className={`w-64 flex-shrink-0 px-3 py-2 flex items-center gap-2 ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''}`}
                  onMouseDown={onReorder ? (e) => {
                    e.preventDefault()
                    dragRef.current = {
                      type: 'reorder',
                      faseId: fase.id,
                      faseIndex: rowIdx,
                      startX: e.clientX,
                      startY: e.clientY,
                      startDuration: fase.durata_prevista,
                      scrollLeft: scrollContainerRef.current?.scrollLeft ?? 0,
                    }
                  } : undefined}
                >
                  {getStatusIcon(fase)}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {artNum != null && (
                        <span className="flex-shrink-0 text-xs font-bold bg-indigo-100 text-indigo-700 px-1.5 py-0 rounded">
                          A{artNum}
                        </span>
                      )}
                      <p className="text-sm font-medium text-gray-900 truncate">{fase.titolo}</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <span>{fase.durata_prevista}g x {numPartecipanti} = {oreFase}h</span>
                      {fase.tipo === 'parallelo' && <span>| Par.</span>}
                    </div>
                    {(fase.partecipanti_ids?.length > 0 || fase.strumenti_ids?.length > 0) && (
                      <div className="flex items-center gap-2 mt-0.5">
                        {fase.partecipanti_ids?.length > 0 && (
                          <span className="flex items-center gap-0.5 text-xs text-blue-600">
                            <Users className="w-3 h-3" /> {fase.partecipanti_ids.length}
                          </span>
                        )}
                        {fase.strumenti_ids?.length > 0 && (
                          <span className="flex items-center gap-0.5 text-xs text-amber-600">
                            <Wrench className="w-3 h-3" /> {fase.strumenti_ids.length}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Barra */}
                <div className="relative py-2 flex-1" style={{ width: chartWidth }}>
                  {/* Griglia */}
                  {dayLabels.map((dl, i) => (
                    <div key={i} className="absolute top-0 bottom-0 border-l border-gray-50" style={{ left: dl.day * dayWidth }} />
                  ))}

                  {/* Barra sfondo (pianificata) */}
                  <div
                    className={`absolute top-2 h-7 rounded ${getBarBgColor(fase)} transition-all`}
                    style={{
                      left: startDay * dayWidth,
                      width: Math.max(durataMostrata * dayWidth, dayWidth),
                    }}
                  />

                  {/* Barra effettiva */}
                  {!isRelative && (
                    <div
                      className={`absolute top-2 h-7 rounded ${getBarColor(fase)} opacity-80`}
                      style={{
                        left: startDay * dayWidth,
                        width: Math.max((fase.giorni_effettivi > 0 ? actualDays : 0) * dayWidth, fase.giorni_effettivi > 0 ? dayWidth : 0),
                      }}
                    />
                  )}

                  {/* Eccedenza ritardo */}
                  {!isRelative && fase.in_ritardo && (
                    <div
                      className="absolute top-2 h-7 rounded-r bg-red-500 opacity-40"
                      style={{ left: (startDay + plannedDays) * dayWidth, width: (actualDays - plannedDays) * dayWidth }}
                    />
                  )}

                  {/* Etichetta giorni + articolo */}
                  {durataMostrata > 0 && (
                    <div
                      className="absolute top-2.5 h-6 flex items-center gap-1 px-1 text-xs font-medium pointer-events-none"
                      style={{
                        left: startDay * dayWidth + 4,
                        color: !isRelative && (fase.giorni_effettivi > 0 || fase.completata) ? 'white' : '#6B7280',
                      }}
                    >
                      {artNum != null && (
                        <span className="font-bold opacity-80">A{artNum}</span>
                      )}
                      <span>{durataMostrata}g</span>
                    </div>
                  )}

                  {/* Handle resize (bordo destro della barra) */}
                  {onDurataChange && (
                    <div
                      className="absolute top-2 h-7 w-3 cursor-col-resize z-20 flex items-center justify-center group"
                      style={{ left: (startDay + durataMostrata) * dayWidth - 6 }}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        dragRef.current = {
                          type: 'resize',
                          faseId: fase.id,
                          faseIndex: rowIdx,
                          startX: e.clientX,
                          startY: e.clientY,
                          startDuration: fase.durata_prevista,
                          scrollLeft: scrollContainerRef.current?.scrollLeft ?? 0,
                        }
                      }}
                    >
                      <div className="w-1 h-4 bg-gray-400 rounded group-hover:bg-primary-500 transition-colors" />
                    </div>
                  )}

                  {/* Indicatore drop reorder */}
                  {reorderPreview?.toIndex === rowIdx && reorderPreview.fromIndex !== rowIdx && (
                    <div className="absolute inset-0 border-2 border-primary-400 rounded pointer-events-none" />
                  )}

                  {/* Linea OGGI */}
                  {!isRelative && todayOffset >= 0 && todayOffset <= totalDays && (
                    <div className="absolute top-0 bottom-0 border-l-2 border-dashed border-primary-500 z-10" style={{ left: todayOffset * dayWidth }} />
                  )}

                  {/* Linea Consegna */}
                  {!isRelative && consegnaOffset >= 0 && consegnaOffset <= totalDays && (
                    <div className="absolute top-0 bottom-0 border-l-2 border-amber-400 z-10" style={{ left: consegnaOffset * dayWidth }} />
                  )}
                </div>
              </div>
            )
          })}

          {/* Legenda */}
          <div className="flex items-center gap-6 px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
            {isRelative ? (
              <>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-gray-300" /><span>Pianificata</span></div>
                <span className="text-gray-400 italic">Avvia la commessa per visualizzare date e avanzamento</span>
              </>
            ) : (
              <>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-gray-300" /><span>Pianificata</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-blue-500" /><span>In corso</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-green-500" /><span>Completata</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-500" /><span>In ritardo</span></div>
                <div className="flex items-center gap-1.5"><div className="w-0 h-3 border-l-2 border-dashed border-primary-500" /><span>Oggi</span></div>
                <div className="flex items-center gap-1.5"><div className="w-0 h-3 border-l-2 border-amber-400" /><span>Consegna</span></div>
              </>
            )}
            {canDrag && (
              <span className="ml-auto text-gray-300 italic">← trascina etichetta per riordinare · bordo destro per ridimensionare →</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
